/**
 * RBAC Type Definitions
 * Matches backend permission system and provides type safety for frontend
 */

import { CognitoPoolEnum } from ".";

// Resource types in the system
export type Resource =
  | "all"
  | "content"
  | "analytics"
  | "accessCode"
  | "roles"
  | "users";

// Action types
export type Action = "create" | "read" | "update" | "delete" | "*";

/**
 * Permission metadata interface
 * Represents a single permission with its metadata
 */
export interface Permission {
  id: string; // Permission string (e.g., "content:create", "*")
  resource: Resource;
  action: Action;
  description: string;
  group: string; // UI grouping (e.g., "Content Manager", "Analytics Dashboard")
}

/**
 * Role interface
 * Represents a role with its permissions
 */
export interface Role {
  _id: string;
  name: string; // Internal name (e.g., "content_manager")
  displayName: string; // UI-friendly name (e.g., "Content Manager")
  description: string;
  permissions: string[]; // Array of permission strings
  createdAt?: string;
  updatedAt?: string;
}

/**
 * User profile with RBAC data
 */
export interface UserProfile {
  _id: string;
  userSub: string;
  email?: string;
  username?: string;
  displayName?: string;
  roles: Role[]; // Populated roles
  segments?: string[];
  effectivePermissions?: string[]; // Computed from all roles
  cognitoPool?: CognitoPoolEnum;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Simplified user for list views
 */
export interface UserEmailPreferences {
  emailAccountIssues: boolean | null;
  emailFeedbackRequests: boolean | null;
  emailNewsUpdates: boolean | null;
}

export interface UserListItem {
  _id: string;
  userSub: string;
  email?: string;
  username?: string;
  roles: Role[];
  segments?: string[];
  cognitoPool?: CognitoPoolEnum | "unknown";
  createdAt?: string;
  lastLogin?: string;
  hasInvalidRoles?: boolean; // Flag indicating user has invalid role IDs
  invalidRoleIds?: string[]; // Array of invalid role ID strings
  isDeleted?: boolean; // Flag indicating user is marked for deletion
  deletionRequestedAt?: string | null; // Date when deletion was requested
  emailPreferences?: UserEmailPreferences;
}

/**
 * API response types
 */
export interface ListRolesResponse {
  total: number;
  page: number;
  pageSize: number;
  items: Role[];
}

export interface ListUsersResponse {
  total: number;
  page: number;
  pageSize: number;
  items: UserListItem[];
  currentServerPool?: CognitoPoolEnum | null;
}

export interface GetPermissionsResponse {
  permissions: Permission[];
  resources: Record<
    string,
    {
      resource: string;
      group: string;
      actions: Array<{
        id: string;
        action: string;
        description: string;
      }>;
    }
  >;
}

export interface GetUserPermissionsResponse {
  userSub: string;
  roles: Array<{
    _id: string;
    name: string;
    displayName: string;
    permissions: string[];
  }>;
  effectivePermissions: string[];
}

/**
 * Request types
 */
export interface CreateRoleRequest {
  name: string;
  displayName: string;
  description?: string;
  permissions?: string[];
}

export interface UpdateRoleRequest {
  displayName?: string;
  description?: string;
  permissions?: string[];
}

export interface AssignRolesRequest {
  roleIds: string[];
}

/**
 * Permission enum matching backend
 */
export enum PermissionEnum {
  // Global / Owner
  OWNER = "*",

  // Content Manager
  CONTENT_CREATE = "content:create",
  CONTENT_READ = "content:read",
  CONTENT_UPDATE = "content:update",
  CONTENT_DELETE = "content:delete",
  CONTENT_ALL = "content:*",

  // Analytics Dashboard
  ANALYTICS_CREATE = "analytics:create",
  ANALYTICS_READ = "analytics:read",
  ANALYTICS_UPDATE = "analytics:update",
  ANALYTICS_DELETE = "analytics:delete",
  ANALYTICS_ALL = "analytics:*",

  // Access Code Dashboard
  ACCESS_CODE_CREATE = "accessCode:create",
  ACCESS_CODE_READ = "accessCode:read",
  ACCESS_CODE_UPDATE = "accessCode:update",
  ACCESS_CODE_DELETE = "accessCode:delete",
  ACCESS_CODE_ALL = "accessCode:*",

  // Announcement Manager
  ANNOUNCEMENT_CREATE = "announcement:create",
  ANNOUNCEMENT_READ = "announcement:read",
  ANNOUNCEMENT_UPDATE = "announcement:update",
  ANNOUNCEMENT_DELETE = "announcement:delete",
  ANNOUNCEMENT_ALL = "announcement:*",
}

/**
 * Role audit action types
 */
export type RoleAuditActionType =
  | "ROLE_ASSIGNED"
  | "ROLE_REMOVED"
  | "MARKED_FOR_DELETION"
  | "DELETION_CANCELLED"
  | "ACCOUNT_DELETED"
  | "CSV_EXPORT";

/**
 * User Role Audit Log interface
 * Represents an audit log entry for role assignments/removals
 */
export interface UserRoleAuditLog {
  _id: string;
  changedBy: {
    userSub: string;
    userName: string;
    email: string;
  };
  targetUser: {
    userSub: string;
    userName: string;
    email: string;
  };
  role?: {
    roleId: string;
    roleName: string;
    roleDisplayName: string;
  };
  actionType: RoleAuditActionType;
  rolesChanged?: {
    before: string[];
    after: string[];
    beforeNames: string[];
    afterNames: string[];
  };
  metadata?: {
    filters?: Record<string, string>;
    fields?: string[];
    exportedCount?: number;
  };
  timestamp: string;
  createdAt: string;
  updatedAt: string;
}
